/** Stock and decision detail: chart, predicate checklist, catalyst evidence (U031-U034). */
import {
  decodeDetailRow,
  type CatalystEvidence,
  type DetailRow,
  type Gate,
} from "../../contracts/views.ts";
import {
  badge,
  definitions,
  instantCell,
  metric,
  renderFreshnessBar,
  scrollRegion,
} from "../../app/components.ts";
import { createCache } from "../../app/cache.ts";
import { h, setChildren } from "../../app/dom.ts";
import { formatPaise, formatQuantity } from "../../app/format.ts";
import {
  renderStateBanner,
  stateOfEnvelope,
  stateOfError,
  type ViewState,
} from "../../app/state.ts";
import type { ViewContext, ViewHandle } from "../../app/view.ts";
import { safeHref } from "../board/model.ts";
import { handleOf, makeFrame } from "../frame.ts";
import { DARK, LIGHT, drawChart, type Palette } from "./chart.ts";
import { buildSeries, chartNotes, levelValues, type Series } from "./model.ts";

const CACHE = createCache<DetailRow>({
  maxItems: 8,
  maxBytes: 2_000_000,
  sizeOf: (row) => 2048 + row.bars.length * 192,
});

/** Forget every cached decision (account switch, logout, revocation). */
export function clearDetailCache(): void {
  CACHE.clear();
}

/** How many decisions the cache holds (for the resource-bound tests). */
export function detailCacheSize(): number {
  return CACHE.size;
}

/** Pick the chart palette from the colour-scheme preference. */
export function paletteFor(win: Window): Palette {
  return win.matchMedia("(prefers-color-scheme: dark)").matches ? DARK : LIGHT;
}

const GATE_SYMBOL: Readonly<Record<Gate["result"], string>> = {
  PASS: "✓ PASS",
  FAIL: "✗ FAIL",
  MISSING: "? MISSING",
};

function checklist(gates: readonly Gate[]): HTMLElement {
  return scrollRegion("Predicate checklist", checklistTable(gates));
}

function checklistTable(gates: readonly Gate[]): HTMLElement {
  return h(
    "table",
    { class: "checklist", attrs: { "aria-label": "Predicate checklist" } },
    h(
      "thead",
      {},
      h(
        "tr",
        {},
        ...[
          "Check",
          "Role",
          "Scope",
          "Result",
          "Disposition",
          "Observed",
          "Observed at",
          "Record",
        ].map((title) => h("th", { attrs: { scope: "col" } }, title)),
      ),
    ),
    h(
      "tbody",
      {},
      ...gates.map((gate) =>
        h(
          "tr",
          { attrs: { "data-gate": gate.name, "data-result": gate.result } },
          h("td", {}, gate.name),
          h("td", {}, gate.role),
          h("td", {}, gate.scope),
          h(
            "td",
            {},
            badge(
              GATE_SYMBOL[gate.result],
              gate.result === "PASS" ? "ok" : gate.result === "FAIL" ? "bad" : "warn",
            ),
          ),
          h("td", {}, gate.disposition),
          h("td", {}, metric(gate.observed)),
          h("td", {}, instantCell(gate.observedAt)),
          h("td", {}, metric(gate.recordRef)),
        ),
      ),
    ),
  );
}

function evidence(catalyst: CatalystEvidence | null): HTMLElement {
  if (catalyst === null) {
    return renderStateBanner({ kind: "no-observations" });
  }
  const link = safeHref(catalyst.sourceUrl);
  return h(
    "div",
    { class: "catalyst" },
    definitions([
      ["Event", catalyst.label],
      [
        "Source",
        link === null
          ? "source link withheld (not an https address)"
          : h(
              "a",
              { attrs: { href: link, rel: "noopener noreferrer nofollow", target: "_blank" } },
              "open source",
            ),
      ],
      ["Published", instantCell(catalyst.publishedAt)],
      ["Received", instantCell(catalyst.receivedAt)],
      ["Classified", instantCell(catalyst.classifiedAt)],
      ["Classification version", catalyst.classificationVersion],
      ["Current revision", String(catalyst.revision)],
    ]),
    h("blockquote", { class: "excerpt" }, catalyst.excerpt),
    h(
      "ol",
      { class: "revisions", attrs: { "aria-label": "Evidence revisions" } },
      ...catalyst.revisions.map((revision) =>
        h(
          "li",
          {},
          `Revision ${String(revision.revision)} (${revision.classificationVersion}): received `,
          instantCell(revision.receivedAt),
          ", classified ",
          instantCell(revision.classifiedAt),
        ),
      ),
    ),
  );
}

function summary(detail: DetailRow, series: Series): HTMLElement {
  const lastPoint = series.points[series.points.length - 1];
  return h(
    "div",
    { class: "chart-summary" },
    h(
      "p",
      {},
      `${String(series.points.length)} plotted point(s)${series.aggregated ? " (aggregated)" : ""}; ` +
        (lastPoint === undefined
          ? "no bars."
          : `last bar is ${lastPoint.developing ? "developing" : "complete"}; volume ${formatQuantity(lastPoint.volume)}.`),
    ),
    h("ul", {}, ...chartNotes(detail, series).map((note) => h("li", {}, note))),
    definitions([
      [
        "Opening range",
        `${detail.levels.openingRangeLowPaise === null ? "—" : formatPaise(detail.levels.openingRangeLowPaise)} to ${detail.levels.openingRangeHighPaise === null ? "—" : formatPaise(detail.levels.openingRangeHighPaise)}`,
      ],
      [
        "Entry",
        metric(detail.levels.entryPaise === null ? null : formatPaise(detail.levels.entryPaise)),
      ],
      [
        "Stop",
        metric(detail.levels.stopPaise === null ? null : formatPaise(detail.levels.stopPaise)),
      ],
      [
        "Exit",
        metric(detail.levels.exitPaise === null ? null : formatPaise(detail.levels.exitPaise)),
      ],
      ["Level values", String(levelValues(detail.levels).length)],
    ]),
  );
}

function render(detail: DetailRow, ctx: ViewContext, win: Window): HTMLElement {
  const series = buildSeries(detail);
  const canvas = h("canvas", {
    class: "chart",
    attrs: {
      width: "720",
      height: "320",
      role: "img",
      "aria-label": `${detail.listing} price chart with opening range, VWAP, entry, stop and exit levels; text summary follows`,
    },
  });
  const drawing = canvas.getContext("2d");
  if (drawing !== null) {
    drawChart(drawing, series, detail.levels, { width: 720, height: 320 }, paletteFor(win));
  }
  const live = detail.provenance.source === "LIVE_CAPTURE";
  ctx.scope.add(() => {
    canvas.width = 0;
  });
  return h(
    "div",
    { class: "detail" },
    h(
      "p",
      {
        class: `provenance ${live ? "live" : "replay"}`,
        attrs: { "data-provenance": detail.provenance.source },
      },
      badge(live ? "LIVE CAPTURE" : "REPLAY (not live execution)", live ? "ok" : "warn"),
      ` engine ${detail.provenance.engineVersion}, bars ${detail.provenance.barSourceVersion}`,
    ),
    definitions([
      ["Listing", detail.listing],
      ["Decision time", instantCell(detail.decisionAt)],
      ["Snapshot", metric(detail.references.snapshotId)],
      ["Feature snapshot", metric(detail.references.featureSnapshotId)],
      ["Assessment", metric(detail.references.assessmentId)],
      ["Manifest", detail.references.manifestFingerprint.slice(0, 12)],
    ]),
    h("h2", {}, "Chart"),
    canvas,
    summary(detail, series),
    h("h2", {}, "Predicate checklist"),
    checklist(detail.gates),
    h("h2", {}, "Catalyst evidence"),
    evidence(detail.catalyst),
  );
}

/** Mount the decision detail for `#/opportunities/detail/<decisionId>`. */
export function mountDetail(ctx: ViewContext, win: Window = window): ViewHandle {
  const frame = makeFrame("detail", "Stock and decision detail");
  const id = ctx.route.param;
  if (id === null) {
    setChildren(frame.banner, [renderStateBanner({ kind: "no-observations" })]);
    return handleOf(frame, ctx);
  }
  const key = `${ctx.accountId ?? "none"}|${id}`;
  const cached = CACHE.get(key);
  const showing = (state: ViewState): void => {
    setChildren(frame.banner, state.kind === "ready" ? [] : [renderStateBanner(state)]);
  };
  if (cached === undefined) {
    showing({ kind: "loading" });
  } else {
    showing({ kind: "stale", ageUs: null, superseded: false });
    setChildren(frame.body, [render(cached, ctx, win)]);
  }
  void ctx.client
    .read("detail", decodeDetailRow, { accountId: ctx.accountId, entityId: id, pageRows: 1 })
    .then((answer) => {
      if (answer === null || ctx.scope.disposed) {
        return;
      }
      const row = answer.page.rows[0];
      showing(stateOfEnvelope(answer));
      setChildren(frame.freshness, [
        renderFreshnessBar(answer, { streamSequence: ctx.streamSequence.get() }),
      ]);
      if (row === undefined) {
        setChildren(frame.body, []);
        return;
      }
      CACHE.set(key, row);
      setChildren(frame.body, [render(row, ctx, win)]);
    })
    .catch((error: unknown) => {
      showing(stateOfError(error));
    });
  return handleOf(frame, ctx);
}
